import React from 'react';
import { motion } from 'motion/react';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';

interface MenuItemCardProps {
  /** Image ID of the menu item */
  imageId?: string | null;
  /** Name of the menu item */
  name: string;
  /** Price in the lowest unit of the currency */
  price: number;
  /** Category name (e.g., 'STARTERS') */
  category: string;
  /** Currency code (e.g., 'GBP') */
  currency: string;
  /** Click handler */
  onClick?: () => void;
  /** Optional additional classes */
  className?: string;
}

/**
 * MenuItemCard displays a food or drink item according to the premium design specifications.
 * Features a large image, category and status badges, and price overlay.
 */
export const MenuItemCard: React.FC<MenuItemCardProps> = ({
  imageId,
  name,
  price,
  category,
  currency,
  onClick,
  className = '',
}) => {
  const hasValidImage = imageId && imageId !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(imageId, 'image').url : null;
  const formattedPrice = formatCurrencyForDisplay(price, currency);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`group relative aspect-square w-full overflow-hidden rounded-2xl bg-[#111111] cursor-pointer shadow-xl ${className}`}
    >
      {/* Background Image */}
      {imageUrl ? (
        <img 
          src={imageUrl} 
          alt={name} 
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a]">
           <span className="text-gray-600 font-medium tracking-widest uppercase text-xs">No Image</span>
        </div>
      )}

      {/* Gradient Overlay for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

      {/* Badges Container */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-start pointer-events-none">
        {/* Category Badge */}
        <div className="bg-[#e2e8f0]/90 backdrop-blur-sm text-[#1e293b] text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-widest shadow-md">
          {category.replace(/_/g, ' ')}
        </div>
      </div>

      {/* Footer Details */}
      <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 pointer-events-none">
        <h3 className="text-white font-bold text-base sm:text-lg lg:text-xl leading-snug drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          {name}
        </h3>
        <div className="text-white font-black text-base sm:text-lg lg:text-xl whitespace-nowrap drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
          {formattedPrice}
        </div>
      </div>
    </motion.div>
  );
};

export default MenuItemCard;
